<template>
    <div class="farm-list-table">
        <el-table
            :data="data"
            :empty-text="translate('no_data')"
            @selection-change="handleSelectionChange"
        >
            <el-table-column type="selection" width="55"></el-table-column>
            <el-table-column prop="name" :label="translate('name_label')" />
            <el-table-column prop="ruc" :label="translate('ruc_label')" />
            <el-table-column
                align="right"
                prop="volume"
                :label="translate('aggregated_volume_label')"
                :formatter="formatNumber"
            />
            <el-table-column :label="translate('actions')" align="right">
                <template class="" v-slot:default="scope">
                    <el-dropdown
                        class="action-button"
                        trigger="click"
                        @click="scope"
                    >
                        <span class="el-dropdown-link">
                            <i class="mdi mdi-dots-vertical" />
                        </span>
                        <el-dropdown-menu v-slot="dropdown">
                            <el-dropdown-item
                                icon="el-icon-view"
                                @click.native="handleView(scope.row)"
                            >
                                {{
                                    $t(
                                        'contrato_rastreabilidade.words.view_analysis'
                                    )
                                }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                icon="el-icon-download"
                                :disabled="scope.row.invoice_attachments && scope.row.invoice_attachments.length == 0"
                                @click.native="handleDownload(scope.row)"
                            >
                                {{
                                    $t(
                                        'contrato_rastreabilidade.words.download_invoice'
                                    )
                                }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                icon="el-icon-delete"
                                @click.native="handleRemove(scope.row)"
                            >
                                {{
                                    $t(
                                        'contrato_rastreabilidade.words.delete_farm'
                                    )
                                }}
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </template>
            </el-table-column>
        </el-table>
        <dialog-eligibility-analysis
            v-if="dialogEligibilityVisible"
            :data="currentProperty"
            v-model="dialogEligibilityVisible"
        />
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import DialogEligibilityAnalysis from '@/modules/traceability-contract/views/components/dialog-eligibility-analysis/index.vue';
import formatters from '@/utils/formatters';
import TerritorioService from '@/services/TerritorioService';

export default {
    name: 'FarmListDefault',
    components: { DialogEligibilityAnalysis },
    props: {
        value: {
            type: Array,
            default: () => []
        },
        selected: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            dialogEligibilityVisible: false,
            currentProperty: {}
        };
    },
    computed: {
        ...mapState('traceabilityContractModule', ['form']),
        data: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        }
    },
    methods: {
        ...mapActions('traceabilityContractModule', [
            'downloadInvoices',
            'updateTraceabilityReport',
            'fetchAvailableBalance',
            'validateAvailableBalance'
        ]),
        translate(key, params = {}) {
            const base = 'contrato_rastreabilidade.messages.farm_list';
            return this.$t(`${base}.${key}`, params);
        },
        getLocale(locale, district = false) {
            const localeComplete = locale
                .split(' - ')
                .map(item => item.trim());
            return district ? localeComplete[0] : localeComplete[1];
        },
        async handleView(row) {
            let district = '--';
            let department = '--';
            await TerritorioService.findLocalidadeById(row.territory_id)
                .then(({data}) => {
                    district = this.getLocale(data, true);
                    department = this.getLocale(data, false);
                })
                .catch(() => {});

            this.currentProperty = {
                name: row.nome,
                department: department,
                district: district,
                area: row.area,
                eligibility: row.eligibility
            };
            this.dialogEligibilityVisible = true;
        },
        async handleRemove(row) {
            await this.$messageBox
                .confirm(
                    this.$tc(
                        'contrato_rastreabilidade.messages.modal_delete_selected_farm',
                        1
                    ),
                    this.$tc(
                        'contrato_rastreabilidade.messages.modal_delete_selected_farm_title',
                        1
                    ),
                    {
                        confirmButtonText: this.$t(
                            'contrato_rastreabilidade.words.confirm'
                        ),
                        cancelButtonText: this.$t(
                            'contrato_rastreabilidade.words.cancel'
                        )
                    }
                )
                .then(async () => {
                    const index = this.data.findIndex(
                        item => item.name === row.name
                    );
                    const removed = this.data.splice(index, 1);

                    await this.fetchAvailableBalance({
                        id: row.territory_id,
                        locale: this.$i18n.locale,
                        extraArea: -removed.volume,
                        form: this.form
                    });

                    if (row.id) {
                        await this.deleteAllDetailFiles({detailId: row.id});
                    }
                })
                .catch(() => {});
        },
        async handleDownload(row) {
            if (row.id) {
                const response = await this.downloadInvoices({
                    detailId: row.id
                });
                if (response.data) {
                    const blob = new Blob([response.data], {
                        type: 'application/zip'
                    });
                    const fileURL = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = fileURL;
                    a.download = 'invoice.zip';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(fileURL);
                } else {
                    this.$notify.error({
                        message: this.$t(
                            'contrato_rastreabilidade.messages.errors.transaction_volume_exceeded_error'
                        )
                    });
                }
            } else {
                this.$notify.error({
                    message: this.$t(
                        'contrato_rastreabilidade.messages.errors.need_save_before'
                    )
                });
            }
        },
        handleSelectionChange(selection) {
            this.$emit('update:selected', selection);
        },
        formatNumber(row, column, reportVolume, index) {
            return formatters.formatNumber(reportVolume);
        }
    }
};
</script>

<style scoped lang="scss">
.farm-list-table {
    .el-table {
        .cell {
            word-break: break-word !important;
        }

        .action-button {
            cursor: pointer;
        }
    }
}
</style>
